import { CheckCircle2, Info, AlertTriangle, XCircle, Bell } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/Tooltip";
import { timeAgo } from "@/utils/dateUtils";
import { cn } from "@/lib/utils";
import { notificationLink } from "@/utils/notificationLink";
import { Button } from "../ui/Button";

// Per-type icon + tone (see themes/styles/tones.js) — theme-aware in both light and dark.
// Full class strings (not interpolated) so Tailwind's build-time scanner picks them up.
// info is blue, not amber: amber and orange read as the same colour at 14px, which left
// info and warning indistinguishable.
const TYPE_STYLES = {
  success: { icon: CheckCircle2, iconWrap: "bg-tone-emerald-soft", iconColor: "text-tone-emerald" },
  info: { icon: Info, iconWrap: "bg-tone-blue-soft", iconColor: "text-tone-blue" },
  warning: { icon: AlertTriangle, iconWrap: "bg-tone-orange-soft", iconColor: "text-tone-orange" },
  error: { icon: XCircle, iconWrap: "bg-tone-rose-soft", iconColor: "text-tone-rose" },
  default: { icon: Bell, iconWrap: "bg-tone-slate-soft", iconColor: "text-tone-slate" },
};

const getTypeStyle = (type) => TYPE_STYLES[type?.toLowerCase()] ?? TYPE_STYLES.default;

/**
 * One row in the bell. A click opens it: it goes where the notification points,
 * whatever its type (`notificationLink` is the one place that maps the two), and
 * marks it read on the way. A row with nowhere to go falls back to marking read,
 * and once read it is inert.
 *
 * A read row that has a destination stays clickable. Reading a notification and
 * going where it points are separate intents, and the second one outlives the
 * first: the reader who cleared the badge still wants to reach the candidate.
 */
const NotificationItem = ({ notification, onOpen }) => {
  const isUnread = !notification.is_read;
  const hasDestination = notificationLink(notification) !== null;
  const isClickable = isUnread || hasDestination;
  const { icon: Icon, iconWrap, iconColor } = getTypeStyle(notification.message_type);

  const row = (
    <Button
      type="button"
      onClick={() => isClickable && onOpen(notification)}
      disabled={!isClickable}
      className={cn(
        "group flex w-full items-start gap-2.5 rounded-xl border p-2.5 text-left transition-all duration-150",
        isUnread
          ? "cursor-pointer border-border-primary bg-background-tertiary hover:border-tone-blue/30 hover:bg-tone-blue-soft/40"
          : cn("border-transparent bg-transparent opacity-55", isClickable ? "cursor-pointer hover:bg-background-tertiary hover:opacity-80" : "cursor-default"),
      )}
    >
      {/* Type icon */}
      <div className={cn("flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg", isUnread ? iconWrap : "bg-background-tertiary")}>
        <Icon size={14} className={isUnread ? iconColor : "text-text-muted"} />
      </div>

      <div className="min-w-0 flex-1">
        {/* Title + Time */}
        <div className="flex items-start justify-between gap-3">
          <p className={cn("text-xs leading-5", isUnread ? "font-semibold text-text-primary" : "font-medium text-text-secondary")}>
            {notification.title ?? "Notification"}
          </p>

          <span className="flex-shrink-0 text-[10px] text-text-muted">{timeAgo(notification.created_at)}</span>
        </div>

        {/* Message */}
        {notification.message && <p className="mt-0.5 line-clamp-2 text-[11px] leading-5 text-text-secondary">{notification.message}</p>}
      </div>

      {/* Unread indicator */}
      {isUnread && <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-tone-blue" aria-hidden="true" />}
    </Button>
  );

  if (!isClickable) return row;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{row}</TooltipTrigger>
      <TooltipContent side="left" sideOffset={6} avoidCollisions={false} className="border border-border-primary text-text-primary">
        {hasDestination ? "Click to open" : "Click to mark as read"}
      </TooltipContent>
    </Tooltip>
  );
};

export default NotificationItem;
